import React, { useCallback } from 'react';
import {
  PAGE_SIZE,
  TEXT_STRING,
  queryKeys,
  SALON_SITE_URL,
} from '../../constants';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getChannelList, getClientChannelList } from 'api';
import { Header, Icon, PageWrapper, Text, TextField } from 'components';
import { TTextFieldChangeEvent } from 'types';
import ChannelList from 'components/ChannelList';
import { useUserStore, useAppStore } from 'store';
import { useMessage, useToast } from 'hooks';
import { trackSalonInquiryBannerClick } from 'utils';
import { VirtuosoHandle } from 'react-virtuoso';
import { usePubNub } from 'pubnub-react';
import SALON_INQUIRY_BANNER_URL from '../../assets/images/teco_salon_banner0813.webp';

const MSG_STRs = TEXT_STRING.MESSAGE;
let timeout: NodeJS.Timeout;

function Message() {
  const navigate = useNavigate();
  const { isAuthenticated, clientId, user } = useUserStore();
  const pubnub = usePubNub();
  const { toastError } = useToast();
  const { findOrCreateMgmChannelAndNavigate } = useMessage({ pubnub });
  const { messageVirtuosoState, setMessageVirtuosoState } = useAppStore();

  const virtuosoRef = React.useRef<VirtuosoHandle>(null);

  // State
  const [keyword, setKeyword] = React.useState<string>('');
  const [searchText, setSearchText] = React.useState<string>('');
  const [loading, setLoading] = React.useState<boolean>(false);

  // Fetch api
  const {
    data,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: [queryKeys.CHANNEL_LIST, searchText],
    queryFn: async ({ pageParam }) =>
      getChannelList(pageParam, PAGE_SIZE, searchText),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages, lastPageParam, allPageParams) => {
      return lastPage?.totalPage > lastPageParam
        ? lastPageParam + 1
        : undefined;
    },
    staleTime: 1000,
    enabled: !!isAuthenticated,
  });

  const { data: clientData } = useInfiniteQuery({
    queryKey: [queryKeys.CLIENT_CHANNEL_LIST, searchText],
    queryFn: async ({ pageParam }) =>
      getClientChannelList(pageParam, PAGE_SIZE, clientId || '', searchText),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages, lastPageParam, allPageParams) => {
      return lastPage?.totalPage > lastPageParam
        ? lastPageParam + 1
        : undefined;
    },
    staleTime: 1000,
    enabled: !isAuthenticated && !!clientId,
  });

  const handleFindOrCreateMgmChannelAndNavigate = useCallback(async () => {
    setLoading(true);
    await findOrCreateMgmChannelAndNavigate();
    setLoading(false);
  }, [findOrCreateMgmChannelAndNavigate]);

  const handleOpenSalonInquiryChannel = useCallback(() => {
    trackSalonInquiryBannerClick('message', user?.id);
    try {
      window.open(SALON_SITE_URL, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toastError(error instanceof Error ? error : new Error(String(error)));
    }
  }, [toastError, user?.id]);

  const onChangeSearch = React.useCallback((e: TTextFieldChangeEvent) => {
    setKeyword(e.target.value);
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      setSearchText(e.target.value);
    }, 500);
  }, []);

  const channels = React.useMemo(
    () =>
      (isAuthenticated
        ? data?.pages.map(({ data }) => data).flat()
        : clientData?.pages.map(({ data }) => data).flat()) ?? [],
    [data, clientData, isAuthenticated],
  );

  const handleChannelClick = useCallback(() => {
    virtuosoRef.current?.getState((state) => {
      setMessageVirtuosoState(state);
    });
  }, [setMessageVirtuosoState]);

  return (
    <PageWrapper className="px-4 overflow-hidden text-sm flex flex-col">
      <Header
        title={<Text>{MSG_STRs.MESSAGE_LIST}</Text>}
        onBack={() => {
          navigate(-1);
        }}
      />
      <TextField
        name="search"
        onChange={onChangeSearch}
        value={keyword}
        placeholder={MSG_STRs.ENTER_NAME}
        prefix={<Icon icon="searchInput" />}
        className="my-4 mr-4 px-2"
      />
      <img
        className="mb-4 cursor-pointer"
        src={SALON_INQUIRY_BANNER_URL}
        alt={TEXT_STRING.SALON_INQUIRY_BANNER.ALT}
        onClick={handleOpenSalonInquiryChannel}
      />
      <ChannelList
        channels={[...channels]}
        fetchMore={fetchNextPage}
        hasMore={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isLoading={isLoading || loading}
        refetch={!!isAuthenticated ? refetch : () => {}}
        createChannelWithAdmin={handleFindOrCreateMgmChannelAndNavigate}
        virtuosoRef={virtuosoRef}
        virtuosoState={messageVirtuosoState}
        onChannelClick={handleChannelClick}
        isSearching={!!searchText}
      />
    </PageWrapper>
  );
}

export default Message;
